import React from "react";
import { useNavigate } from "react-router-dom";
import { Sparkles, LayoutDashboard, LayoutGrid, BellRing, GraduationCap, X, ArrowRight } from "lucide-react";
import { useExperience } from "../features/experience";

const CHANGES = [
  { icon: LayoutDashboard, title: "Home shows what matters today", body: "Your Home page now opens with the handful of things that need your attention, and nothing else." },
  { icon: LayoutGrid, title: "Your life, in hubs", body: "Travel, Vehicles, Property, Pets and more each have a home. Choose yours in the Hub Manager; hiding one never deletes anything." },
  { icon: BellRing, title: "One Reminders list", body: "Responsibilities have become Reminders: a single list of everything due, while your records live in the hubs they belong to." },
  { icon: GraduationCap, title: "PlannioLife Academy", body: "Two-minute lessons on every feature, whenever you want a refresher." },
];

// One-time orientation for existing users after the redesign. Framed as a benefit, not a
// version number. Dismissing it hands over to a quiet Home banner until the lesson is read.
export default function WelcomeToPlannio() {
  const { experience, markFirstRun } = useExperience();
  const navigate = useNavigate();
  if (!experience?.v2_intro?.show_modal) return null;

  const explore = () => markFirstRun("v2_intro");
  const tour = () => { markFirstRun("v2_intro_done"); navigate("/community/academy/a-simpler-plannio"); };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="welcome-v2-modal"
      role="dialog" aria-modal="true" aria-label="Welcome to the new PlannioLife">
      <div className="absolute inset-0 bg-black/45" onClick={explore} />
      <div className="relative z-10 flex max-h-[90vh] w-full max-w-lg flex-col overflow-hidden rounded-3xl bg-white shadow-2xl">
        <div className="relative bg-[#0F766E] px-7 pb-6 pt-7 text-white">
          <button data-testid="welcome-v2-close" onClick={explore} aria-label="Close"
            className="absolute right-4 top-4 rounded-lg p-1.5 text-white/70 transition-colors hover:bg-white/10 hover:text-white">
            <X className="h-5 w-5" />
          </button>
          <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-white/15">
            <Sparkles className="h-6 w-6" strokeWidth={1.7} />
          </span>
          <h2 className="mt-4 font-display text-2xl font-semibold leading-tight">Welcome to a simpler PlannioLife</h2>
          <p className="mt-2 text-sm leading-relaxed text-white/85">
            We've reorganised PlannioLife to help you find things faster, reduce clutter and make everyday organisation feel simpler.
          </p>
        </div>
        <div className="overflow-y-auto px-7 py-5">
          <ul className="space-y-4">
            {CHANGES.map((c) => (
              <li key={c.title} className="flex gap-3.5">
                <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-[#0F766E]/8">
                  <c.icon className="h-5 w-5 text-[#0F766E]" strokeWidth={1.7} />
                </span>
                <div>
                  <div className="text-sm font-semibold text-[#2D3436]">{c.title}</div>
                  <div className="mt-0.5 text-sm leading-relaxed text-[#636E72]">{c.body}</div>
                </div>
              </li>
            ))}
          </ul>
        </div>
        <div className="flex flex-col gap-2 border-t border-[#EBECE8] p-5 sm:flex-row-reverse">
          <button data-testid="welcome-v2-tour" onClick={tour}
            className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-[#0F766E] px-5 py-2.5 font-medium text-white transition-transform hover:-translate-y-0.5 hover:shadow-lg">
            See what's changed <ArrowRight className="h-4 w-4" />
          </button>
          <button data-testid="welcome-v2-explore" onClick={explore}
            className="flex-1 rounded-xl border border-[#E2E8F0] px-5 py-2.5 font-medium text-[#636E72] transition-colors hover:bg-[#F4F6F2]">
            Explore on my own
          </button>
        </div>
      </div>
    </div>
  );
}
